/* /sites — projects, client sites, properties, warehouses or branches (named after the company's industry) with the people allocated
 * to each, monthly labour cost and margin against the contract value. API: GET/POST /sites, PUT /sites/{id}. */
import "./industry.css";
import { useMemo, useState } from "react";
import { useCreateSite, useSettings, useSites, useUpdateSite } from "../../data/hooks";
import type { SiteKind, WorkSite, WorkSiteInput } from "../../data/types";
import { industryOf, siteCost } from "../../domain/industry";
import { useI18n } from "../../i18n";
import { gDate } from "../../lib/format";
import {
  Affix, Button, Chip, EmptyState, ErrorBox, Field, Input, Kpi, Modal, Money, PageHeader, SearchBox, Seg, SelectField, Skeleton, TextField, Toggle, errMsg, useToast,
} from "../../ui";
import { useEmpMap, useRole } from "../pay/components/common";
import { curL } from "../../lib/country";

export const SITE_KINDS: [SiteKind, string, string][] = [["project", "Project", "مشروع"], ["client", "Client site", "موقع عميل"], ["property", "Property / outlet", "منشأة / منفذ"], ["warehouse", "Warehouse", "مستودع"], ["branch", "Branch", "فرع"]];

type F = { name: string; nameAr: string; kind: SiteKind; client: string; city: string; start: string; end: string; outdoor: boolean; allowance: string; contractValue: string; status: WorkSite["status"]; employeeIds: number[] };
const num = (v: string) => (v.trim() === "" ? 0 : Number(v));

/** Same rules as POST/PUT /sites (contracts/API.md "Work sites"). */
export function validateSite(f: F): Record<string, string> {
  const e: Record<string, string> = {};
  if (!f.name.trim()) e.name = "required";
  else if ([...f.name.trim()].length > 120) e.name = "max 120 characters";
  if ([...f.nameAr.trim()].length > 120) e.nameAr = "max 120 characters";
  if ([...f.client.trim()].length > 120) e.client = "max 120 characters";
  if ([...f.city.trim()].length > 60) e.city = "max 60 characters";
  if (f.start && f.end && f.end < f.start) e.end = "must be on or after start";
  const a = num(f.allowance);
  if (!Number.isFinite(a) || a < 0 || a > 100000) e.allowance = "must be between 0 and 100000";
  const c = num(f.contractValue);
  if (!Number.isFinite(c) || c < 0 || c > 100000000) e.contractValue = "must be between 0 and 100000000";
  return e;
}

function SiteModal({ site, defaultKind, showContract, onClose }: { site: WorkSite | null; defaultKind: SiteKind; showContract: boolean; onClose: () => void }) {
  const { L, t, name } = useI18n();
  const toast = useToast();
  const emps = useEmpMap();
  const sitesQ = useSites();
  const create = useCreateSite();
  const update = useUpdateSite();
  const [f, setF] = useState<F>(site
    ? { name: site.name, nameAr: site.nameAr, kind: site.kind, client: site.client, city: site.city, start: site.start, end: site.end, outdoor: site.outdoor, allowance: site.allowance ? String(site.allowance) : "", contractValue: site.contractValue ? String(site.contractValue) : "", status: site.status, employeeIds: site.employeeIds }
    : { name: "", nameAr: "", kind: defaultKind, client: "", city: "", start: "", end: "", outdoor: false, allowance: "", contractValue: "", status: "Active", employeeIds: [] });
  const [touched, setTouched] = useState(false);
  const [srv, setSrv] = useState<Record<string, string>>({});
  const [q, setQ] = useState("");
  const errs = { ...(touched ? validateSite(f) : {}), ...srv };
  const set = <K extends keyof F>(k: K) => (v: F[K]) => { setF((x) => ({ ...x, [k]: v })); setSrv({}); };
  const elsewhere = useMemo(() => {
    const m = new Map<number, string>();
    for (const x of sitesQ.data ?? []) if (x.status === "Active" && x.id !== site?.id) for (const id of x.employeeIds) m.set(id, x.name);
    return m;
  }, [sitesQ.data, site]);
  const people = emps.list.filter((e) => e.status !== "Terminated" && (!q.trim() || (e.nameEn + " " + e.nameAr + " " + e.code + " " + e.title).toLowerCase().includes(q.trim().toLowerCase())));
  const toggle = (id: number) => set("employeeIds")(f.employeeIds.includes(id) ? f.employeeIds.filter((x) => x !== id) : [...f.employeeIds, id]);
  const msg = (k: string) => {
    const v = errs[k];
    if (!v) return undefined;
    if (v === "required") return L("Enter a name", "أدخل الاسم");
    if (v === "must be on or after start") return L("End must be on or after the start date", "يجب أن يكون تاريخ النهاية بعد البداية أو مساويًا لها");
    if (v.startsWith("max")) return L("Too long", "النص طويل جدًا");
    if (v.startsWith("must be between")) return L("Enter an amount in range", "أدخل مبلغًا ضمن الحدود");
    return v;
  };
  const submit = async () => {
    setTouched(true);
    if (Object.keys(validateSite(f)).length) return;
    const input: WorkSiteInput = { name: f.name.trim(), nameAr: f.nameAr.trim(), kind: f.kind, client: f.client.trim(), city: f.city.trim(), start: f.start, end: f.end, outdoor: f.outdoor,
      allowance: num(f.allowance), contractValue: num(f.contractValue), status: f.status, employeeIds: f.employeeIds };
    try {
      const r = site ? await update.mutateAsync({ id: site.id, patch: input }) : await create.mutateAsync(input);
      toast(site ? L("Saved · ", "تم الحفظ · ") + r.id : L("Added · ", "تمت الإضافة · ") + r.id);
      onClose();
    } catch (e) {
      const fe = (e as { fields?: Record<string, string> }).fields;
      if (fe && Object.keys(fe).length) setSrv(fe); else toast(errMsg(e), "bad");
    }
  };
  const moved = f.status === "Active" ? f.employeeIds.filter((id) => elsewhere.has(id)).length : 0;
  return (
    <Modal title={site ? L("Edit ", "تعديل ") + site.id : L("Add", "إضافة")} onClose={onClose} testId="site-modal"
      foot={<><Button onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" onClick={() => { void submit(); }} loading={create.isPending || update.isPending} data-testid="site-submit">{t("Save")}</Button></>}>
      <div className="stack">
        <div className="form2">
          <TextField label={L("Name (English)", "الاسم (إنجليزي)")} value={f.name} onChange={set("name")} err={msg("name")} req testId="site-name" />
          <TextField label={L("Name (Arabic)", "الاسم (عربي)")} value={f.nameAr} onChange={set("nameAr")} err={msg("nameAr")} dir="rtl" testId="site-name-ar" />
          <SelectField label={t("Type")} value={f.kind} onChange={(v) => set("kind")(v as SiteKind)} options={SITE_KINDS.map(([k, en, ar]): [string, string] => [k, L(en, ar)])} testId="site-kind" />
          <TextField label={L("Client / owner", "العميل / المالك")} value={f.client} onChange={set("client")} err={msg("client")} testId="site-client" />
          <TextField label={L("City", "المدينة")} value={f.city} onChange={set("city")} err={msg("city")} testId="site-city" />
          <SelectField label={t("Status")} value={f.status} onChange={(v) => set("status")(v as WorkSite["status"])} options={[["Active", L("Active", "نشط")], ["Closed", L("Closed", "مغلق")]]} testId="site-status" />
          <TextField label={L("Start", "البداية")} type="date" value={f.start} onChange={set("start")} err={msg("start")} testId="site-start" />
          <TextField label={L("End", "النهاية")} type="date" value={f.end} onChange={set("end")} err={msg("end")} testId="site-end" />
          <Field label={L("Site allowance / person / month", "بدل الموقع / شخص / شهر")} err={msg("allowance")} htmlFor="site-allow" hint={L("Used for costing", "لأغراض التكلفة")}>
            <Affix pre={curL(L)}><Input id="site-allow" className="num" type="number" min={0} value={f.allowance} onChange={(e) => set("allowance")(e.target.value)} bad={!!errs.allowance} data-testid="site-allowance" /></Affix>
          </Field>
          {(showContract || !!site?.contractValue) && (
            <Field label={L("Contract value / month", "قيمة العقد / شهريًا")} err={msg("contractValue")} htmlFor="site-cv" hint={L("Shows the margin", "لعرض الهامش")}>
              <Affix pre={curL(L)}><Input id="site-cv" className="num" type="number" min={0} value={f.contractValue} onChange={(e) => set("contractValue")(e.target.value)} bad={!!errs.contractValue} data-testid="site-contract" /></Affix>
            </Field>
          )}
        </div>
        <Toggle checked={f.outdoor} onChange={set("outdoor")} label={L("Outdoor work (summer midday ban applies)", "عمل في مكان مكشوف (ينطبق حظر الظهيرة صيفًا)")} testId="site-outdoor" />
        <Field label={L(`People on this site (${f.employeeIds.length})`, `الموظفون في هذا الموقع (${f.employeeIds.length})`)} err={srv.employeeIds}>
          <div className="stack" style={{ gap: 6 }}>
            <SearchBox value={q} onChange={setQ} placeholder={L("Search employee…", "ابحث عن موظف…")} testId="site-emp-search" />
            <div className="site-emps" data-testid="site-emps">
              {people.map((e) => (
                <label key={e.id}>
                  <input type="checkbox" checked={f.employeeIds.includes(e.id)} onChange={() => toggle(e.id)} data-testid={"site-emp-" + e.id} />
                  <span className="sm"><b>{name(e)}</b> <span className="muted">· {e.code} · {e.title}</span>{elsewhere.has(e.id) && <span className="xs muted"> · {L("now at ", "حاليًا في ")}{elsewhere.get(e.id)}</span>}</span>
                </label>
              ))}
              {people.length === 0 && <span className="sm muted" style={{ padding: 8 }}>{L("No employees match", "لا يوجد موظفون مطابقون")}</span>}
            </div>
            {moved > 0 && <span className="xs muted" data-testid="site-moved">{L(`${moved} selected ${moved === 1 ? "person moves" : "people move"} from another active site.`, `سيُنقل ${moved} من موقع نشط آخر.`)}</span>}
          </div>
        </Field>
      </div>
    </Modal>
  );
}

export default function Sites() {
  const { L, lang } = useI18n();
  const { can } = useRole();
  const sQ = useSettings();
  const q = useSites();
  const emps = useEmpMap();
  const [filter, setFilter] = useState<"Active" | "Closed" | "all">("Active");
  const [search, setSearch] = useState("");
  const [edit, setEdit] = useState<WorkSite | "new" | null>(null);
  const ind = industryOf(sQ.data?.tenant.category);
  const all = q.data ?? [];
  const list = useMemo(() => {
    const s = search.trim().toLowerCase();
    return all.filter((x) => (filter === "all" || x.status === filter) && (!s || [x.name, x.nameAr, x.client, x.city, x.id].some((v) => v.toLowerCase().includes(s))));
  }, [all, filter, search]);
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data || !sQ.data) return <Skeleton />;
  const label = ind ? ind.sites.label : (["Work sites", "مواقع العمل"] as [string, string]);
  const showContract = ind ? ind.sites.contract : true;
  const active = all.filter((x) => x.status === "Active");
  const costs = new Map(all.map((x) => [x.id, siteCost(x, emps.list)]));
  const totalCost = active.reduce((s, x) => s + costs.get(x.id)!.total, 0);
  const totalContract = active.reduce((s, x) => s + x.contractValue, 0);
  const kindL = (k: SiteKind) => { const r = SITE_KINDS.find((x) => x[0] === k)!; return L(r[1], r[2]); };
  const allocated = new Set(active.flatMap((x) => x.employeeIds)).size;

  return (
    <div data-testid="sites">
      <PageHeader title={L(...label)} sub={L("Who works where, what each site costs per month and the margin against its contract.", "من يعمل أين، وتكلفة كل موقع شهريًا، والهامش مقابل عقده.")}
        actions={can("hr.modules") ? <Button variant="pri" icon="plus" onClick={() => setEdit("new")} data-testid="btn-add-site">{L("Add", "إضافة")}</Button> : undefined} />
      <div className="g g-4 mb">
        <Kpi testId="kpi-active-sites" label={L("Active", "نشطة")} value={String(active.length)} icon="map-pin" />
        <Kpi testId="kpi-allocated" label={L("People allocated", "الموظفون المخصصون")} value={String(allocated)} icon="users" tone="info" foot={L(`of ${emps.list.filter((e) => e.status !== "Terminated").length} active employees`, `من ${emps.list.filter((e) => e.status !== "Terminated").length} موظفًا نشطًا`)} />
        <Kpi testId="kpi-site-cost" label={L("Labour cost / month", "تكلفة العمالة / شهريًا")} value={<Money value={totalCost} dec={0} plain />} unit={curL(L)} icon="wallet" tone="gold" />
        {showContract
          ? <Kpi testId="kpi-margin" label={L("Margin on contracts", "هامش العقود")} value={totalContract ? <Money value={totalContract - active.filter((x) => x.contractValue > 0).reduce((s, x) => s + costs.get(x.id)!.total, 0)} dec={0} plain /> : "—"} unit={totalContract ? curL(L) : undefined} icon="chart" foot={L("Sites with a contract value", "المواقع التي لها قيمة عقد")} />
          : <Kpi testId="kpi-outdoor" label={L("Outdoor sites", "مواقع مكشوفة")} value={String(active.filter((x) => x.outdoor).length)} icon="sun" />}
      </div>
      <section className="card">
        <div className="toolbar">
          <SearchBox value={search} onChange={setSearch} placeholder={L("Search name, client or city…", "ابحث بالاسم أو العميل أو المدينة…")} testId="site-search" />
          <Seg label={L("Status", "الحالة")} value={filter} onChange={setFilter} items={[["Active", L("Active", "نشطة")], ["Closed", L("Closed", "مغلقة")], ["all", L("All", "الكل")]]} />
        </div>
        {list.length === 0 ? (
          <EmptyState title={all.length ? L("Nothing matches", "لا توجد نتائج") : L("No sites yet", "لا توجد مواقع بعد")}
            desc={all.length ? undefined : L("Add your first project, client site or branch and allocate people to it.", "أضف أول مشروع أو موقع عميل أو فرع وخصص له الموظفين.")}
            action={!all.length && can("hr.modules") ? <Button variant="pri" icon="plus" onClick={() => setEdit("new")}>{L("Add", "إضافة")}</Button> : undefined} />
        ) : (
          <div className="tw">
            <table className="tbl cards" data-testid="sites-table">
              <thead><tr><th>{L("Name", "الاسم")}</th><th className="hide-md">{L("Client", "العميل")}</th><th className="hide-md">{L("Period", "الفترة")}</th><th className="end">{L("People", "الموظفون")}</th><th className="end">{L("Cost / month", "التكلفة / شهريًا")}</th>{showContract && <th className="end hide-md">{L("Margin", "الهامش")}</th>}<th>{L("Status", "الحالة")}</th><th className="end" aria-label={L("Actions", "إجراءات")} /></tr></thead>
              <tbody>
                {list.map((x) => {
                  const c = costs.get(x.id)!;
                  return (
                    <tr key={x.id} data-testid={"site-row-" + x.id}>
                      <td className="k1"><b>{L(x.name, x.nameAr || x.name)}</b><div className="xs muted">{x.id} · {kindL(x.kind)}{x.city ? " · " + x.city : ""}{x.outdoor ? L(" · outdoor", " · مكشوف") : ""}</div></td>
                      <td className="hide-md">{x.client || "—"}</td>
                      <td className="hide-md sm">{x.start ? gDate(x.start, lang) : "—"}{x.end ? " → " + gDate(x.end, lang) : ""}</td>
                      <td className="end num" data-l={L("People", "الموظفون")} data-testid="site-headcount">{c.headcount}</td>
                      <td className="end" data-l={L("Cost / month", "التكلفة / شهريًا")} data-testid="site-cost"><Money value={c.total} dec={0} /></td>
                      {showContract && <td className="end hide-md" data-testid="site-margin">{c.margin == null ? "—" : <span className={c.margin < 0 ? "neg" : ""}><Money value={c.margin} dec={0} /> <span className="xs muted">({c.marginPct}%)</span></span>}</td>}
                      <td className="k2"><Chip status={x.status} tone={x.status === "Active" ? "ok" : ""}>{x.status === "Active" ? L("Active", "نشط") : L("Closed", "مغلق")}</Chip></td>
                      <td className="end">{can("hr.modules") && <Button variant="sm ghost" icon="edit" onClick={() => setEdit(x)} data-testid={"site-edit-" + x.id}>{L("Edit", "تعديل")}</Button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {edit && <SiteModal site={edit === "new" ? null : edit} defaultKind={ind?.sites.kind ?? "project"} showContract={showContract} onClose={() => setEdit(null)} />}
    </div>
  );
}
